import React, { useEffect, useState } from "react";
import { RefreshCw, Check, AlertTriangle, X, Circle, ExternalLink, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Integration Health card — admin/manager only.
 * Lights up green per integration once each is configured + reachable.
 * Surfaces hints from the backend (e.g. "set GEMINI_API_KEY in backend/.env").
 */
export default function IntegrationStatus() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const loadStatus = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get("/integrations/status");
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load integration status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    api.get("/integrations/status")
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => { if (!cancelled) setErr(e.response?.data?.detail || "Failed to load integration status"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const overall = data?.ok;

  return (
    <section className="swiss-card-flat p-5 mt-6" data-testid="integration-status-card">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="label" style={{ color: "var(--accent-hover)" }}>
            Self-host health
          </div>
          <h2 className="font-display text-xl font-semibold mt-1 flex items-center gap-2">
            Integration status
            {data && (
              <span
                className="tag"
                style={{
                  background: overall ? "var(--accent-soft)" : "#FEE2E2",
                  color: overall ? "var(--accent-hover)" : "#991B1B",
                }}
                data-testid="integration-overall-status"
              >
                {overall ? "all green" : "needs attention"}
              </span>
            )}
          </h2>
          <p className="muted text-xs mt-1 max-w-xl">
            Live check of MongoDB, Gemini, Resend, storage and webhooks. Use this after editing
            <code className="font-mono"> backend/.env</code> on your server to verify each integration
            is wired correctly.
          </p>
        </div>
        <button
          onClick={loadStatus}
          disabled={loading}
          className="btn-ghost text-xs"
          data-testid="integration-refresh-btn"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 p-3 rounded">{err}</div>}

      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="integration-checks-grid">
          {Object.entries(data.checks).map(([key, check]) => (
            <CheckCard key={key} name={key} {...check} />
          ))}
        </div>
      )}
    </section>
  );
}

function EmailTestButton() {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/email/test");
      if (data.ok) {
        toast.success(data.message || `Test email sent to ${data.to}`);
      } else {
        toast.error(`Send failed: ${data.error || "unknown"}`, {
          description: data.hint,
          duration: 10000,
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test email request failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] font-semibold uppercase tracking-wider transition-colors"
      style={{
        borderColor: "var(--accent)",
        color: "var(--accent-hover)",
        background: "var(--accent-soft)",
      }}
      data-testid="integration-email-test-btn"
    >
      <Send size={10} />
      {busy ? "Sending…" : "Send test email"}
    </button>
  );
}

function MonitoringSyncButton({ onSynced }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/monitoring/sync-now", {});
      const ok = data.ok_count || 0;
      const skip = data.skipped_count || 0;
      const err = data.error_count || 0;
      if (err > 0) {
        toast.warning(`Sync done: ${ok} ok, ${skip} skipped, ${err} errors`, {
          description: data.errors?.[0]?.error,
          duration: 10000,
        });
      } else {
        toast.success(`Sync done: ${ok} project${ok === 1 ? "" : "s"} updated, ${skip} skipped (no API key or site_id)`);
      }
      onSynced && onSynced();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync request failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] font-semibold uppercase tracking-wider transition-colors"
      style={{
        borderColor: "var(--accent)",
        color: "var(--accent-hover)",
        background: "var(--accent-soft)",
      }}
      data-testid="integration-monitoring-sync-btn"
    >
      <RefreshCw size={10} className={busy ? "animate-spin" : ""} />
      {busy ? "Syncing…" : "Sync now"}
    </button>
  );
}

const STATUS_STYLES = {
  ok:    { icon: Check,          bg: "var(--accent-soft)", border: "var(--accent)",   fg: "var(--accent-hover)" },
  warn:  { icon: AlertTriangle,  bg: "var(--warm-soft)",   border: "var(--warm)",     fg: "var(--warm-hover)" },
  error: { icon: X,              bg: "#FEE2E2",            border: "#EF4444",         fg: "#991B1B" },
  off:   { icon: Circle,         bg: "#F4F8F8",            border: "var(--line)",     fg: "var(--ink-2)" },
};

const PRETTY_NAME = {
  mongodb:  "MongoDB",
  vision:   "Gemini (AI extraction)",
  email:    "Resend (Emails)",
  storage:  "Object storage",
  webhooks: "Webhooks",
  monitoring_sync: "Monitoring auto-sync",
  security: "JWT secret",
  base_url: "Public URL",
};

function CheckCard({ name, status, detail, hint }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.off;
  const Icon = s.icon;
  const label = PRETTY_NAME[name] || name;

  // Linkify any URLs inside the hint text.
  const renderHint = (h) => {
    if (!h) return null;
    const parts = h.split(/(https?:\/\/[^\s)]+)/g);
    return parts.map((part, i) =>
      /^https?:\/\//.test(part) ? (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noreferrer"
          className="underline inline-flex items-center gap-0.5"
          style={{ color: "var(--accent-hover)" }}
        >
          {part.replace(/^https?:\/\//, "")}
          <ExternalLink size={9} />
        </a>
      ) : (
        <span key={i}>{part}</span>
      )
    );
  };

  return (
    <div
      className="p-3 rounded-md border bg-white"
      style={{ borderColor: s.border }}
      data-testid={`integration-check-${name}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="font-medium text-sm">{label}</div>
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
          style={{ background: s.bg, color: s.fg }}
          data-testid={`integration-check-${name}-status`}
        >
          <Icon size={10} />
          {status}
        </span>
      </div>
      <div className="text-[11px]" style={{ color: "var(--ink-2)" }} data-testid={`integration-check-${name}-detail`}>
        {detail}
      </div>
      {hint && (
        <div className="text-[10px] mt-2 muted leading-relaxed" data-testid={`integration-check-${name}-hint`}>
          {renderHint(hint)}
        </div>
      )}
      {name === "email" && status !== "off" && <EmailTestButton />}
      {name === "monitoring_sync" && <MonitoringSyncButton />}
    </div>
  );
}
